import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Cookie, Settings, X } from "lucide-react";

type ConsentCategories = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
};

const STORAGE_KEY = "cookie-consent";

const getStoredConsent = (): ConsentCategories | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return null;
};

const saveConsent = (categories: ConsentCategories) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(categories));
};

const GA_ID = "G-0TZLDQ8VJB";
const ADS_ID = "AW-676791405";
const GTM_ID = "GTM-MCNTK34D";

const getGtag = () => {
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  if (!w.gtag) {
    w.gtag = function () {
      w.dataLayer.push(arguments);
    };
  }
  return w.gtag;
};

// Send a Consent Mode v2 update based on the user's choice
const updateConsent = (categories: ConsentCategories) => {
  if (typeof window === "undefined") return;
  const gtag = getGtag();
  const analytics = categories.analytics ? "granted" : "denied";
  const marketing = categories.marketing ? "granted" : "denied";
  gtag("consent", "update", {
    analytics_storage: analytics,
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
  });
  (window as any).__consentApplied = true;
};

export const COOKIE_SETTINGS_EVENT = "open-cookie-settings";

// Let any part of the app reopen the consent banner (GDPR: withdraw consent)
export const openCookieSettings = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(COOKIE_SETTINGS_EVENT));
};

const loadScriptOnce = (
  id: string,
  build: () => HTMLScriptElement,
): HTMLScriptElement | null => {
  if (document.getElementById(id)) return null;
  const el = build();
  el.id = id;
  document.head.appendChild(el);
  return el;
};

// Advanced Consent Mode: the Google tag always loads, consent state governs storage.
const loadGoogleTags = () => {
  if (typeof window === "undefined") return;
  const w = window as any;
  const gtag = getGtag();

  const configureGoogle = () => {
    if (w.__googleConfigured) return;
    w.__googleConfigured = true;
    gtag("config", GA_ID);
    gtag("config", ADS_ID);
  };

  const existing = document.getElementById("ga-gtag-js") as HTMLScriptElement | null;
  if (existing) {
    configureGoogle();
  } else {
    const ga = loadScriptOnce("ga-gtag-js", () => {
      const s = document.createElement("script");
      s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
      s.async = true;
      return s;
    });
    if (ga) ga.onload = configureGoogle;
  }

  // GTM
  if (!w.__gtmLoaded && !document.getElementById("gtm-script")) {
    w.__gtmLoaded = true;
    w.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
    const j = document.createElement("script");
    j.id = "gtm-script";
    j.async = true;
    j.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
    document.head.appendChild(j);
  }
};

// Non-Google marketing tools require explicit marketing consent
const loadMarketingTools = () => {
  if (typeof window === "undefined") return;

  loadScriptOnce("hs-script-loader", () => {
    const hs = document.createElement("script");
    hs.type = "text/javascript";
    hs.async = true;
    hs.defer = true;
    hs.src = "//js-eu1.hs-scripts.com/144838652.js";
    return hs;
  });

  const ap = loadScriptOnce("apollo-tracker", () => {
    const n = Math.random().toString(36).substring(7);
    const s = document.createElement("script");
    s.src =
      "https://assets.apollo.io/micro/website-tracker/tracker.iife.js?nocache=" +
      n;
    s.async = true;
    s.defer = true;
    return s;
  });
  if (ap) {
    ap.onload = () => {
      (window as any).trackingFunctions?.onLoad({
        appId: "69c047762cf7fa000da415ff",
      });
    };
  }

  // Mailchimp Connected Sites (moved out of index.html, consent-gated)
  loadScriptOnce("mcjs", () => {
    const mc = document.createElement("script");
    mc.async = true;
    mc.src =
      "https://chimpstatic.com/mcjs-connected/js/users/47432cbcc5110f849888e5ce5/2d238049bf2955987bbca120d.js";
    return mc;
  });
};

const loadTracking = (categories: ConsentCategories) => {
  loadGoogleTags();
  if (categories.marketing) loadMarketingTools();
};

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [categories, setCategories] = useState<ConsentCategories>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const stored = getStoredConsent();
    if (stored) {
      setCategories(stored);
      // index.html already applied the stored choice; only update if it did not
      if (!(window as any).__consentApplied) updateConsent(stored);
      loadTracking(stored);
    } else {
      // Advanced consent mode: tag loads with default denied (cookieless pings)
      loadGoogleTags();
      setVisible(true);
    }

    const reopen = () => {
      const current = getStoredConsent();
      if (current) setCategories(current);
      setShowSettings(true);
      setVisible(true);
    };
    window.addEventListener(COOKIE_SETTINGS_EVENT, reopen);
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, reopen);
  }, []);

  const accept = (cats: ConsentCategories) => {
    saveConsent(cats);
    updateConsent(cats);
    loadTracking(cats);
    setVisible(false);
  };

  const acceptAll = () =>
    accept({ necessary: true, analytics: true, marketing: true });

  const acceptNecessary = () =>
    accept({ necessary: true, analytics: false, marketing: false });

  const acceptCustom = () => accept(categories);

  if (!visible) return null;


  return (
    <div className="no-print fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6 animate-in slide-in-from-bottom-4 duration-500">
      <div className="max-w-2xl mx-auto bg-background border border-border rounded-2xl shadow-2xl p-5 md:p-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Cookie className="h-5 w-5 text-accent shrink-0" />
            <h3 className="font-semibold text-foreground text-base">
              Vi använder cookies
            </h3>
          </div>
          <button
            onClick={acceptNecessary}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Stäng"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
          Vi använder cookies för att förbättra din upplevelse och analysera
          trafik. Läs mer i vår{" "}
          <a
            href="/integritetsskydd-cookies-och-gdpr"
            className="text-accent hover:underline font-medium"
          >
            integritetspolicy
          </a>
          .
        </p>

        {/* Settings panel */}
        {showSettings && (
          <div className="space-y-3 mb-4 p-3 bg-muted/50 rounded-lg border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">
                  Nödvändiga
                </p>
                <p className="text-xs text-muted-foreground">
                  Krävs för att sidan ska fungera
                </p>
              </div>
              <Switch checked disabled />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">Analys</p>
                <p className="text-xs text-muted-foreground">
                  Google Analytics, Tag Manager
                </p>
              </div>
              <Switch
                checked={categories.analytics}
                onCheckedChange={(v) =>
                  setCategories((c) => ({ ...c, analytics: v }))
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">
                  Marknadsföring
                </p>
                <p className="text-xs text-muted-foreground">
                  Google Ads, HubSpot, Apollo
                </p>
              </div>
              <Switch
                checked={categories.marketing}
                onCheckedChange={(v) =>
                  setCategories((c) => ({ ...c, marketing: v }))
                }
              />
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-2">
          {showSettings ? (
            <Button
              onClick={acceptCustom}
              className="flex-1"
              variant="default"
            >
              Spara val
            </Button>
          ) : (
            <Button
              onClick={() => setShowSettings(true)}
              variant="outline"
              className="flex-1 gap-1.5"
            >
              <Settings className="h-4 w-4" />
              Anpassa
            </Button>
          )}
          <Button onClick={acceptNecessary} variant="outline" className="flex-1">
            Bara nödvändiga
          </Button>
          <Button onClick={acceptAll} className="flex-1">
            Godkänn alla
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
